import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusMessage } from '@hvcgroep/design-system';

import Button from '../../../../shared/components/Button';
import Switch from '../../../../shared/components/Switch';
import { APP_ID } from '../../../../shared/config/constants';
import gtm from '../../../../shared/helpers/gtm';
import { AddressFull } from '../../../../shared/types/opzet';

import BlockCtaPersonalAgenda from '../../components/Blocks/CtaPersonalAgenda';
import SkeletonLoader from '../../components/SkeletonLoader';
import TodayButton from '../../components/Navigation/TodayButton';
import NavigationViewMonth from '../../components/Navigation/ViewMonth';
import NavigationViewYear from '../../components/Navigation/ViewYear';
import useCalendarDate from '../../hooks/useCalendarDate';
import useCalendarType from '../../hooks/useCalendarType';
import useCalendarView from '../../hooks/useCalendarView';
import useDownloadUrl from '../../hooks/useDownloadUrl';
import ViewCalendarMonth from './ViewCalendarMonth';
import ViewCalendarYear from './ViewCalendarYear';
import ViewList from './ViewList';

import './style.scss';
import { useGetWasteStreamsQuery } from '../../../../shared/store/slices/WebsiteApi';
import { opzetApi } from '../../../../shared/store/slices/OpzetApi';

interface Props {
  address: AddressFull;
}

const TITLE_ID = 'waste-calendar-title';
const SWITCH_ID_PREFIX = 'switch';

const Widget: React.FC<Props> = (props) => {
  const { address } = props;
  const { t } = useTranslation();
  const { status: contentStatus } = useGetWasteStreamsQuery(null);

  const { data = [], status } = opzetApi.endpoints.getCalendarItems.useQuery(address.bagid);

  const [date, setDate] = useCalendarDate();
  const [widgetType, setWidgetType] = useCalendarType();
  const [widgetView, setWidgetView] = useCalendarView();
  const tabPanelRef = React.useRef<HTMLDivElement>(null);

  const downloadPdfUrl = useDownloadUrl(address, date);

  const isLoading = (
    status === 'pending' ||
    contentStatus === 'pending' ||
    status === 'uninitialized' ||
    contentStatus === 'uninitialized'
  );

  const toggleMonthAndYearView = () => {
    if (widgetView === 'month') {
      setWidgetView('year');
    } else {
      setWidgetView('month');
    }

    if (tabPanelRef.current) {
      tabPanelRef.current.focus();
    }
  };

  // Empty state
  if (status === 'fulfilled' && data.length === 0) {
    return (
      <StatusMessage type="info">
        {t('waste_calendar.states.empty.pickup_dates')}
      </StatusMessage>
    );
  }

  return (
    <div className="CalendarWidget">
      <div className="CalendarWidget__header">
        <TodayButton
          className="CalendarWidget__header__today"
          onClick={() => {
            gtm.trackUserEvent(String(APP_ID), 'List view navigation click', t('waste_calendar.navigation.today'));
            setDate(new Date());
          }}
        />

        {(widgetType === 'calendar' && widgetView === 'month') && (
          <NavigationViewMonth currentDate={date} updateDate={setDate} />
        )}

        {(widgetType === 'calendar' && widgetView === 'year') && (
          <NavigationViewYear currentDate={date} updateDate={setDate} />
        )}

        <Switch
          selected={widgetType}
          tabPanelRef={tabPanelRef}
          idPrefix={SWITCH_ID_PREFIX}
          onChange={(key) => {
            if (key === 'list') {
              setWidgetType('list');
            }

            if (key === 'calendar') {
              setWidgetType('calendar');
            }
          }}
          switches={[
            {
              key: 'list',
              children: t('waste_calendar.navigation.view_switch.list'),
              className: 'icon-before',
              icon: 'list',
            },
            {
              key: 'calendar',
              children: t('waste_calendar.navigation.view_switch.calendar'),
              className: 'icon-before',
              icon: 'calendar_month',
            },
          ]}
        />
      </div>
      {isLoading && (
        <SkeletonLoader widgetType={widgetType} />
      )}
      {(status === 'rejected' || contentStatus === 'rejected') && (
        <StatusMessage type="error">
          {t('waste_calendar.states.error.pickup_dates')}
        </StatusMessage>
      )}
      {(status === 'fulfilled' && contentStatus === 'fulfilled') && (
        <div
          className="CalendarWidget__widget"
        >
          <div
            className="CalendarWidget__tabpanel"
            ref={tabPanelRef}
            id={`CalendarWidget-${widgetType}`}
            role="tabpanel"
            aria-labelledby={`${SWITCH_ID_PREFIX}-${widgetType}`}
            aria-describedby={TITLE_ID}
          >
            {(widgetView === 'month' && widgetType === 'calendar') && (
              <ViewCalendarMonth items={data} date={date} />
            )}
            {(widgetView === 'year' && widgetType === 'calendar') && (
              <ViewCalendarYear items={data} date={date} />
            )}
            {widgetType === 'list' && (
              <ViewList items={data} date={date} />
            )}
          </div>

          <div className="CalendarWidget__cta-buttons">
            {(widgetView === 'month' && widgetType === 'calendar') && (
              <Button
                type="secondary"
                variation="anchor"
                onClick={toggleMonthAndYearView}
              >
                {t('waste_calendar.buttons.view_year_calendar')}
              </Button>
            )}

            {(widgetView === 'year' && widgetType === 'calendar') && (
            <Button type="secondary" icon="arrow_upward" onClick={toggleMonthAndYearView}>
              {t('waste_calendar.buttons.view_month_calendar')}
            </Button>
            )}

            {downloadPdfUrl && (
              <Button type="secondary" href={downloadPdfUrl} icon="download" target="_blank">
                {t('waste_calendar.buttons.download_pdf', { year: date.getFullYear() })}
              </Button>
            )}
          </div>

          <BlockCtaPersonalAgenda />
        </div>
      )}
    </div>
  );
};

export default Widget;
